import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Package, Plus, Pencil, Save, X, Loader2, Search, AlertTriangle, ArrowUpDown, Gamepad2, Monitor, Joystick, AlertCircle, ChevronDown, Archive } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import type { Product, ProductCategory } from '../types/products';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1550745165-9bc0b252726f';

const PLATFORMS = ['ALL', 'PlayStation 5', 'PlayStation 4', 'Xbox Series X/S', 'Xbox One', 'Nintendo Switch', 'PC'];
const CATEGORIES: ProductCategory[] = ['Games', 'Consoles', 'Peripherals'];
const INITIAL_EXPANDED_CATEGORIES = CATEGORIES.reduce((acc, category) => {
  acc[category] = false;
  return acc;
}, {} as Record<ProductCategory, boolean>);

const STOCK_FILTERS = [
  { label: 'All Stock', value: 'all' },
  { label: 'Low Stock (< 5)', value: 'low' },
  { label: 'Out of Stock', value: 'out' },
  { label: 'In Stock', value: 'in' }
];

interface EditableProduct extends Product {
  isEditing: boolean;
}

type SortDirection = 'asc' | 'desc';

export default function Stock() {
  const { user } = useAuth();
  const staffDetails = user?.staff_details ? JSON.parse(user.staff_details) : null;
  const isManager = staffDetails?.role === 'manager' || user?.role === 'store_manager';
  const canEditPrices = isManager || 
                        user?.role === 'admin' || 
                        user?.permissions?.can_amend_prices === true;
  const [products, setProducts] = useState<EditableProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<EditableProduct | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('ALL');
  const [stockFilter, setStockFilter] = useState('all');
  const [nameSort, setNameSort] = useState<SortDirection>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(INITIAL_EXPANDED_CATEGORIES);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [showLowStockView, setShowLowStockView] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'Games' as ProductCategory,
    price: '',
    description: '',
    image_url: '',
    stock_quantity: '',
    platform: '',
    release_date: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>('Games');
  const [showLowStockAlert, setShowLowStockAlert] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [productToArchive, setProductToArchive] = useState<EditableProduct | null>(null);
  const [categoryLowStockCounts, setCategoryLowStockCounts] = useState<Record<ProductCategory, number>>({
    Games: 0,
    Consoles: 0,
    Peripherals: 0
  });
  const [platformLowStockCounts, setPlatformLowStockCounts] = useState<Record<string, number>>(
    PLATFORMS.reduce((acc, platform) => {
      acc[platform] = 0;
      return acc;
    }, {} as Record<string, number>)
  );

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .order('name');

        if (error) throw error;

        setProducts(data.map(product => ({
          ...product,
          isEditing: false
        })) as EditableProduct[]);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const startEditing = (product: EditableProduct) => {
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const cancelEditing = () => {
    setEditingProduct(null);
    setShowEditModal(false);
  };

  const saveChanges = async (updatedProduct: EditableProduct) => {
    setSaving(true);
    try {
      // Validate release date if provided
      if (updatedProduct.category === 'Games' && updatedProduct.release_date) {
        try {
          new Date(updatedProduct.release_date).toISOString();
        } catch (err) {
          alert('Invalid release date format');
          return;
        }
      }

      const { error } = await supabase
        .from('products')
        .update({
          name: updatedProduct.name,
          category: updatedProduct.category,
          price: updatedProduct.price,
          description: updatedProduct.description,
          image_url: updatedProduct.image_url,
          stock_quantity: updatedProduct.stock_quantity,
          platform: updatedProduct.platform,
          release_date: updatedProduct.release_date || null
        })
        .eq('id', updatedProduct.id);

      if (error) throw error;

      setProducts(prev =>
        prev.map(p =>
          p.id === updatedProduct.id ? updatedProduct : p
        )
      );
      
      setShowEditModal(false);
      setEditingProduct(null);
      setSuccessMessage(`${updatedProduct.name} has been updated successfully`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000); // Hide after 3 seconds
    } catch (err) {
      console.error('Error updating product:', err);
      alert('Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const handlePlatformSelect = (platform: string) => {
    if (platform === 'ALL') {
      // If ALL is selected, toggle between all platforms and none
      setSelectedPlatforms(prev => 
        prev.length === PLATFORMS.length - 1 ? [] : PLATFORMS.filter(p => p !== 'ALL')
      );
    } else {
      setSelectedPlatforms(prev => {
        const isSelected = prev.includes(platform);
        if (isSelected) {
          return prev.filter(p => p !== platform);
        } else {
          return [...prev, platform];
        }
      });
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Use selectedPlatforms, or default to all platforms if none selected
    const platformsToAdd = selectedPlatforms.length > 0 
      ? selectedPlatforms 
      : PLATFORMS.filter(p => p !== 'ALL'); // Default to all actual platforms if none selected
    
    // Validate release date if provided
    if (newProduct.category === 'Games' && newProduct.release_date) {
      try {
        new Date(newProduct.release_date).toISOString();
      } catch (err) {
        setError('Invalid release date format');
        setSaving(false);
        return;
      }
    }

    try {
      // Create a product for each selected platform
      const productsToInsert = platformsToAdd.map(platform => ({
        ...newProduct,
        price: Number(newProduct.price),
        stock_quantity: Number(newProduct.stock_quantity),
        release_date: newProduct.release_date || null,
        platform,
        status: 'active' // Explicitly set status for new products
      }));

      const { data, error } = await supabase
        .from('products')
        .insert(productsToInsert)
        .select();

      if (error) throw error;

      setProducts(prev => [...prev, ...data.map(product => ({ ...product, isEditing: false }))]);
      setShowAddModal(false);
      setSelectedPlatforms([]); // Reset selected platforms
      setNewProduct({
        name: '',
        category: 'Games',
        price: '',
        description: '',
        image_url: '',
        stock_quantity: '',
        platform: '',
        release_date: ''
      });
      
      // Show success message
      setSuccessMessage(`Added ${data.length} new product${data.length > 1 ? 's' : ''} successfully`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const filterProducts = useCallback(
    (productsToFilter: EditableProduct[]) => {
      return productsToFilter.filter(product => {
        // Search filter
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.description.toLowerCase().includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        // Platform filter
        if (selectedPlatform !== 'ALL' && product.platform !== selectedPlatform) {
          return false;
        }

        // Stock filter
        switch (stockFilter) {
          case 'low':
            // When in low stock view, we want both low and out of stock items
            return showLowStockView ? 
              product.stock_quantity < 5 : // Include both low and out of stock
              product.stock_quantity > 0 && product.stock_quantity < 5; // Just low stock
          case 'out':
            return product.stock_quantity === 0;
          case 'in':
            return product.stock_quantity > 0;
          default:
            return true;
        }
      }).sort((a, b) => {
        return nameSort === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      });
    },
    [searchQuery, selectedPlatform, stockFilter, nameSort]
  );

  const productsByCategory = useMemo(() => {
    return products.reduce((acc, product) => {
      if (!acc[product.category]) {
        acc[product.category] = [];
      }
      acc[product.category].push(product);
      return acc;
    }, {} as Record<ProductCategory, EditableProduct[]>);
  }, [products]);

  const filteredProductsByCategory = useMemo(() => {
    return CATEGORIES.reduce((acc, category) => {
      const categoryProducts = productsByCategory[category] || [];
      acc[category] = filterProducts(categoryProducts);
      return acc;
    }, {} as Record<ProductCategory, EditableProduct[]>);
  }, [productsByCategory, filterProducts]);

  // Update expanded categories based on search results
  useEffect(() => {
    if (!searchQuery && selectedPlatform === 'ALL' && stockFilter === 'all') {
      setExpandedCategories(INITIAL_EXPANDED_CATEGORIES);
      return;
    }

    setExpandedCategories(
      CATEGORIES.reduce((acc, category) => ({
        ...acc,
        [category]: filteredProductsByCategory[category]?.length > 0
      }), {} as Record<ProductCategory, boolean>)
    );
  }, [filteredProductsByCategory, searchQuery, selectedPlatform, stockFilter]);

  const toggleCategory = (category: ProductCategory) => {
    // If any product in this category is being edited, don't allow collapse
    if (filteredProductsByCategory[category]?.some(p => p.isEditing)) {
      return;
    }
    
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPlatform('ALL');
    setStockFilter('all');
    setNameSort('asc');
  };

  const filteredProducts = (products: EditableProduct[]) => {
    return filterProducts(products);
  };

  const checkLowStock = useCallback(() => {
    // Toggle between low stock view and normal view
    const newState = !showLowStockView;
    setShowLowStockView(newState);
    
    if (newState) {
      // Switching to low stock view
      const lowStockItems = products.filter(p => p.stock_quantity < 5 && p.stock_quantity > 0);
      const outOfStockItems = products.filter(p => p.stock_quantity === 0);
      const totalLowStockCount = lowStockItems.length + outOfStockItems.length;
      
      // Group by category for detailed feedback
      const categoryCounts = [...lowStockItems, ...outOfStockItems].reduce((acc, product) => {
        acc[product.category] = (acc[product.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      setLowStockCount(totalLowStockCount);
      setStockFilter('low');
      
      // Clear category selection to show all low stock items
      setSelectedCategory('Games');
      setSelectedPlatform('ALL');
      
      // Show success message with category breakdown
      setSuccessMessage(
        Object.entries(categoryCounts)
          .map(([category, count]) => `${count} ${category}`)
          .join(', ') + ' need attention'
      );
      
      setShowLowStockAlert(true);
      setTimeout(() => setShowLowStockAlert(false), 5000);
    } else {
      // Returning to normal view
      setStockFilter('all');
      setSuccessMessage('Returned to normal stock view');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  }, [products, showLowStockView]);

  const handleDeleteClick = (product: EditableProduct) => {
    setProductToArchive(product);
    setShowArchiveConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToArchive) return;
    
    try {
      const { error } = await supabase
        .from('products')
        .update({
          status: 'archived'
        })
        .eq('id', productToArchive.id);

      if (error) throw error;

      // Remove product from state
      setProducts(prev => prev.filter(p => p.id !== productToArchive.id));
      setShowArchiveConfirmation(false);
      setProductToArchive(null);
      
      // Show success message
      setSuccessMessage(`${productToArchive.name} has been archived successfully`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Error archiving product:', err);
      setError('Failed to archive product. Please try again.');
    }
  };

  // Calculate low stock counts per category
  useEffect(() => {
    const counts = CATEGORIES.reduce((acc, category) => {
      acc[category] = products.filter(
        p => p.category === category && (p.stock_quantity === 0 || p.stock_quantity < 5)
      ).length;
      return acc;
    }, {} as Record<ProductCategory, number>);
    
    setCategoryLowStockCounts(counts);
  }, [products]);

  // Calculate low stock counts per platform, filtered by the currently selected category
  useEffect(() => {
    const counts = PLATFORMS.reduce((acc, platform) => {
      // For ALL, count all products with low stock in the current category
      if (platform === 'ALL') {
        acc[platform] = products.filter(
          p => p.category === selectedCategory && (p.stock_quantity === 0 || p.stock_quantity < 5)
        ).length;
      } else {
        // For specific platforms, count only that platform in the current category
        acc[platform] = products.filter(
          p => p.platform === platform && 
             p.category === selectedCategory && 
             (p.stock_quantity === 0 || p.stock_quantity < 5)
        ).length;
      }
      return acc;
    }, {} as Record<string, number>);
    
    setPlatformLowStockCounts(counts);
  }, [products, selectedCategory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto">
      <div className="p-8">
        <div className="space-y-6 max-w-[95%] mx-auto">
          {/* Header */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
            <div className="flex flex-col gap-6">
              {/* Title and Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-500/10 rounded-lg p-2">
                    <Package className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">Stock Management</h1>
                    <p className="text-sm text-white/50">Manage your inventory across all platforms</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={checkLowStock}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      showLowStockView 
                        ? 'bg-purple-600 hover:bg-purple-700 text-white border-2 border-white/30' 
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {showLowStockView ? 'Show All Stock' : 'Show Low Stock'}
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Product
                  </button>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <input
                    type="text"
                    placeholder="Search products by name, description, or platform..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white"
                  >
                    {STOCK_FILTERS.map(({ label, value }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setNameSort(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white flex items-center gap-2 hover:bg-white/10 transition-colors"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    {nameSort === 'asc' ? 'A to Z' : 'Z to A'}
                  </button>
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedCategory('Games')}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedCategory === 'Games' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Gamepad2 className="w-5 h-5" />
                  Games
                  {categoryLowStockCounts.Games > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                      {categoryLowStockCounts.Games}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedCategory('Consoles');
                    setSelectedPlatform('ALL'); // Reset platform filter when selecting Consoles
                  }}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedCategory === 'Consoles' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Monitor className="w-5 h-5" />
                  Consoles
                  {categoryLowStockCounts.Consoles > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                      {categoryLowStockCounts.Consoles}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setSelectedCategory('Peripherals')}
                  className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedCategory === 'Peripherals' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Joystick className="w-5 h-5" />
                  Peripherals
                  {categoryLowStockCounts.Peripherals > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                      {categoryLowStockCounts.Peripherals}
                    </span>
                  )}
                </button>
              </div>

              {/* Platform Selection for Games and Peripherals */}
              {(selectedCategory === 'Games' || selectedCategory === 'Peripherals') && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {PLATFORMS.map(platform => (
                    <button
                      key={platform}
                      onClick={() => setSelectedPlatform(selectedPlatform === platform ? 'ALL' : platform)}
                      className={`px-4 py-2 rounded-lg text-white transition-all flex items-center justify-center gap-2 ${
                        selectedPlatform === platform ? 'bg-white/20 border-2 border-purple-500' : 'bg-white/5 border-2 border-transparent'
                      } hover:bg-white/10`}
                    >
                      {platform}
                      {platform !== 'ALL' && platformLowStockCounts[platform] > 0 && (
                        <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                          {platformLowStockCounts[platform]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts(productsByCategory[selectedCategory] || []).map(product => (
              <div
                key={product.id}
                className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-4 hover:bg-white/15 transition-colors"
              >
                <div className="flex gap-4">
                  <img
                    src={product.image_url || FALLBACK_IMAGE}
                    alt={product.name}
                    className="w-24 h-24 object-cover rounded-lg bg-white/5"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = FALLBACK_IMAGE;
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-lg font-medium text-white truncate">
                        {product.name}
                      </h3>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEditing(product)}
                          className="p-1 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
                        >
                          <Pencil className="w-4 h-4 text-white" />
                        </button>
                        {(isManager || user?.role === 'admin') && (
                          <button
                            onClick={() => handleDeleteClick(product)}
                            className="p-1 hover:bg-red-500/20 rounded-lg transition-colors flex-shrink-0"
                            title="Archive product (remove from store but keep order history)"
                          >
                            <Archive className="w-4 h-4 text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>
                    {product.platform && (
                      <p className="text-white/50 text-sm mt-1">
                        {product.platform}
                      </p>
                    )}
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-xl font-semibold text-white">
                        £{product.price.toFixed(2)}
                      </span>
                      <div className={`px-2 py-0.5 rounded-full text-sm ${
                        product.stock_quantity === 0
                          ? 'bg-red-500/20 text-red-300'
                          : product.stock_quantity < 5
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-green-500/20 text-green-300'
                      }`}>
                        {product.stock_quantity === 0
                          ? 'Out of Stock'
                          : product.stock_quantity < 5
                          ? `Low Stock: ${product.stock_quantity}`
                          : `${product.stock_quantity} in Stock`}
                      </div>
                    </div>
                    {product.category === 'Games' && product.release_date && (
                      <p className="text-white/50 text-sm mt-2">
                        Release Date: {new Date(product.release_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredProducts(productsByCategory[selectedCategory] || []).length === 0 && (
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-8 text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="w-12 h-12 text-white/50" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">No Products Found</h3>
              <p className="text-white/70">
                {searchQuery
                  ? `No products match your search "${searchQuery}"`
                  : `No ${selectedCategory.toLowerCase()} found for the selected filters`}
              </p>
            </div>
          )}

          {/* Low Stock Alert Notification */}
          {showLowStockAlert && (
            <div className="fixed bottom-4 right-4 bg-amber-500/90 text-white px-6 py-3 rounded-lg shadow-lg backdrop-blur-sm border border-amber-400/50 flex items-center gap-3 animate-fade-in-up">
              <AlertCircle className="w-5 h-5" />
              <div>
                <p className="font-medium">Stock Alert</p>
                <p className="text-sm">
                  {successMessage}
                </p>
              </div>
            </div>
          )}

          {/* Success Notification */}
          {showSuccess && (
            <div className="fixed bottom-4 right-4 bg-green-500/90 text-white px-6 py-3 rounded-lg shadow-lg backdrop-blur-sm border border-green-400/50 flex items-center gap-2 animate-fade-in-up">
              <div className="w-2 h-2 bg-white rounded-full" />
              {successMessage}
            </div>
          )}

          {/* Add/Edit Modals */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-white">Add New Product</h2>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                <form onSubmit={handleAddProduct} className="space-y-6">
                  {error && (
                    <div className="p-3 text-sm text-red-200 bg-red-500/20 border border-red-500/20 rounded-lg">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Name</label>
                      <input
                        type="text"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Category</label>
                      <select
                        value={newProduct.category}
                        onChange={(e) => setNewProduct(prev => ({
                          ...prev,
                          category: e.target.value as ProductCategory,
                          // Clear platform if switching to Consoles
                          platform: e.target.value === 'Consoles' ? '' : prev.platform
                        }))}
                        required
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      >
                        {CATEGORIES.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, price: e.target.value }))}
                        required
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Stock Quantity</label>
                      <input
                        type="number"
                        value={newProduct.stock_quantity}
                        onChange={(e) => setNewProduct(prev => ({ ...prev, stock_quantity: e.target.value }))}
                        required
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      />
                    </div>

                    {(newProduct.category === 'Games' || newProduct.category === 'Peripherals') && (
                      <div className="relative">
                        <label className="block text-sm font-medium text-white mb-1">Platforms</label>
                        <button
                          type="button"
                          onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-left flex justify-between items-center"
                        >
                          <span>
                            {selectedPlatforms.length === 0 
                              ? 'Select Platforms' 
                              : selectedPlatforms.length === PLATFORMS.length - 1 
                                ? 'All Platforms'
                                : `${selectedPlatforms.length} Platform${selectedPlatforms.length > 1 ? 's' : ''} Selected`}
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform ${showPlatformDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {showPlatformDropdown && (
                          <div className="absolute z-50 mt-1 w-full bg-[#1a1b26]/95 backdrop-blur-xl border border-white/20 rounded-lg py-1 shadow-xl">
                            {PLATFORMS.map(platform => (
                              <label
                                key={platform}
                                className="flex items-center px-3 py-2 hover:bg-white/10 cursor-pointer gap-3 text-white group"
                              >
                                <div className="relative flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={platform === 'ALL' 
                                      ? selectedPlatforms.length === PLATFORMS.length - 1
                                      : selectedPlatforms.includes(platform)}
                                    onChange={() => handlePlatformSelect(platform)}
                                    className="peer h-4 w-4 opacity-0 absolute"
                                  />
                                  <div className="h-4 w-4 border-2 border-white/40 rounded flex items-center justify-center transition-colors peer-checked:bg-purple-600 peer-checked:border-purple-600 peer-hover:border-purple-400">
                                    {(platform === 'ALL' 
                                      ? selectedPlatforms.length === PLATFORMS.length - 1
                                      : selectedPlatforms.includes(platform)) && (
                                      <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                <span className="select-none">{platform}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {newProduct.category === 'Games' && (
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">Release Date</label>
                        <input
                          type="date"
                          value={newProduct.release_date}
                          onChange={(e) => setNewProduct(prev => ({ ...prev, release_date: e.target.value }))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Description</label>
                    <textarea
                      value={newProduct.description}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                      required
                      rows={3}
                      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Image URL</label>
                    <input
                      type="url"
                      value={newProduct.image_url}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, image_url: e.target.value }))}
                      required
                      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Add Product
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showEditModal && editingProduct && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold text-white">Edit Product</h2>
                  <button
                    onClick={cancelEditing}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Name</label>
                      <input
                        type="text"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, name: e.target.value } : null)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editingProduct.price}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, price: Number(e.target.value) } : null)}
                        disabled={!canEditPrices}
                        className={`w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white ${!canEditPrices ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      {!canEditPrices && (
                        <p className="text-xs text-white/50 mt-1">You don't have permission to edit prices</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Stock Quantity</label>
                      <input
                        type="number"
                        value={editingProduct.stock_quantity}
                        onChange={(e) => setEditingProduct(prev => prev ? { ...prev, stock_quantity: Number(e.target.value) } : null)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                      />
                    </div>
                    {(editingProduct.category === 'Games' || editingProduct.category === 'Peripherals') && (
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">Platform</label>
                        <select
                          value={editingProduct.platform || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, platform: e.target.value } : null)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        >
                          <option value="">Select Platform</option>
                          {PLATFORMS.filter(p => p !== 'ALL').map(platform => (
                            <option key={platform} value={platform}>{platform}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {editingProduct.category === 'Games' && (
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">Release Date</label>
                        <input
                          type="date"
                          value={editingProduct.release_date || ''}
                          onChange={(e) => setEditingProduct(prev => prev ? { ...prev, release_date: e.target.value } : null)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Description</label>
                    <textarea
                      value={editingProduct.description}
                      onChange={(e) => setEditingProduct(prev => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Image URL</label>
                    <input
                      type="url"
                      value={editingProduct.image_url}
                      onChange={(e) => setEditingProduct(prev => prev ? { ...prev, image_url: e.target.value } : null)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={cancelEditing}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                    <button
                      onClick={() => editingProduct && saveChanges(editingProduct)}
                      disabled={saving}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showArchiveConfirmation && productToArchive && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold text-white mb-4">Confirm Archive</h2>
                <p className="text-white/80 mb-6">
                  Are you sure you want to archive <span className="text-white font-medium">{productToArchive.name}</span>? 
                  <br /><br />
                  <span className="text-sm text-white/60">
                    This will remove the product from your store and stock management, but preserve all order history and sales data for reporting.
                  </span>
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowArchiveConfirmation(false);
                      setProductToArchive(null);
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    Archive Product
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}